import React, { useState } from 'react';
import { TextButton, Typography } from 'seagull';
import { routes } from "../../../../constants";
import { ErrorMessage, FormContainer, FormDescription, FormFields, VerticalFormFields, FormTitle, LinkButton } from "../../components/FormComponents";
import { useEventReporter, useStoreActions } from "../../../../contexts";
import { handlePromise } from "../../../../infra/utils";
import OTPField from "./OTPField";
import sharedStyles from "../LoginScreen.module.scss";
import styles from "./OTP.module.scss";
import { useOnMount } from "../../../../hooks/useOnMount";
import { useUtmsParams } from "../../../../hooks/useUtmsParams";
const notices = {
  otpRequestOK: {
    text: "We've emailed you a new verification code. It can take up to a few minutes to arrive.",
    name: 'otpRequestOK',
    displaysInline: false,
    isError: false
  },
  tooManyRequests: {
    text: "It looks like you've tried too many times. Try again later, or reset password.",
    name: 'tooManyRequests',
    displaysInline: false,
    isError: true
  },
  wrongCode: {
    text: 'Code is invalid. Please try again.',
    name: 'wrongCode',
    displaysInline: true,
    isError: true
  },
  unknown: {
    text: 'An unknown error has occured. Please try again later.',
    name: 'unknown',
    displaysInline: false,
    isError: true
  },
  login: {
    text: 'Failed to login, please try again.',
    name: 'login',
    displaysInline: false,
    isError: true
  }
};
// following the best practices as describe here: https://web.dev/sms-otp-form/
export default function OTPStep(_ref) {
  let {
    email,
    hasPassword
  } = _ref;
  const [isLoading, setIsLoading] = useState(false);
  const {
    reportFormSubmission,
    reportToHeap
  } = useEventReporter();
  const {
    api,
    setAuthToken,
    logBI
  } = useStoreActions();
  const getUtms = useUtmsParams();
  const [otpSessionStatus, setOtpSessionStatus] = useState();
  const notice = notices[otpSessionStatus !== null && otpSessionStatus !== void 0 ? otpSessionStatus : ''];
  const inlineError = (notice === null || notice === void 0 ? void 0 : notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
  const floatingError = (notice === null || notice === void 0 ? void 0 : notice.isError) && !(notice !== null && notice !== void 0 && notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
  const floatingMessage = !(notice !== null && notice !== void 0 && notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.text);
  useOnMount(() => {
    api.auth.requestNewOTP(email);
  });

  async function validateExistingOTP(otp) {
    setIsLoading(true);
    const [authToken, error, hasError] = await handlePromise(api.auth.validateOTP(email, otp, getUtms()));

    if (hasError) {
      setOtpSessionStatus(getErrorCause(error));
    } else {
      const [,, authTokenError] = await handlePromise(setAuthToken(authToken));

      if (authTokenError) {
        setOtpSessionStatus('login');
      } else {
        reportFormSubmission('general-logged-in-successfully', null);
        reportToHeap('Login Screen', 'click', 'signed-in-with-six-digits-code', {
          email
        });
        logBI(16001, {
          email
        });
        setOtpSessionStatus(undefined);
      }
    }

    setIsLoading(false);
  }

  async function getNewOTP() {
    logBI(16000, {
      email
    });
    reportToHeap('Login Screen', 'click', 'clicked-send-me-six-digits-code', {
      email
    });
    const [, error, hasError] = await handlePromise(api.auth.requestNewOTP(email));

    if (hasError) {
      setOtpSessionStatus(getErrorCause(error));
    } else {
      setOtpSessionStatus('otpRequestOK');
    }
  }

  function getErrorCause(error) {
    if (error.response) {
      if (error.response.status === 400) {
        return 'wrongCode';
      } else if (error.response.status === 429) {
        return 'tooManyRequests';
      }
    }

    return 'unknown';
  }

  return /*#__PURE__*/React.createElement(FormContainer, {
    onSubmit: e => e.preventDefault(),
    "data-testid": "login-form"
  }, /*#__PURE__*/React.createElement(FormTitle, null, "Check your email"), /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(FormDescription, null, "If there's an account associated with", ' ', /*#__PURE__*/React.createElement(Typography, {
    as: "span",
    weight: "bold"
  }, email), ", you'll receive an email with a six digit code to login.")), /*#__PURE__*/React.createElement(FormFields, null, /*#__PURE__*/React.createElement(VerticalFormFields, null, /*#__PURE__*/React.createElement(OTPField, {
    isLoading: isLoading,
    error: Boolean(inlineError),
    helperText: inlineError,
    autoFocus: true,
    onChange: (code, allDigitsEntered) => {
      if (!allDigitsEntered) return;
      validateExistingOTP(code);
    }
  }), floatingMessage && /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph2",
    skin: "success",
    "data-testid": "info-message"
  }, floatingMessage), floatingError && /*#__PURE__*/React.createElement(ErrorMessage, {
    "data-testid": "err-message",
    "data-err": notice === null || notice === void 0 ? void 0 : notice.name
  }, floatingError))), /*#__PURE__*/React.createElement("div", {
    className: sharedStyles.footer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.requestCode
  }, /*#__PURE__*/React.createElement(Typography, {
    as: "p",
    variant: "paragraph2"
  }, "Still haven\u2019t received a code?"), /*#__PURE__*/React.createElement(Typography, {
    as: "p",
    variant: "paragraph2",
    skin: "secondary"
  }, "Check your spam/updates folder or", ' ', /*#__PURE__*/React.createElement(TextButton, {
    "data-testid": "get-new-code-button",
    size: "small",
    underline: "none",
    onClick: async e => {
      e.preventDefault();
      getNewOTP();
    }
  }, "get a new code"))), hasPassword || /*#__PURE__*/React.createElement(Typography, {
    as: "p",
    variant: "paragraph2",
    skin: "secondary",
    "data-testid": "set-a-password-button"
  }, "Prefer to set a password?", ' ', /*#__PURE__*/React.createElement(LinkButton, {
    toInternalLink: routes.resetPassword,
    "data-testid": "set-a-password"
  }, "Do it here")), /*#__PURE__*/React.createElement(LinkButton, {
    toInternalLink: routes.login,
    "data-testid": "back-link",
    className: sharedStyles.backButton
  }, "Back")));
}